import ModalWrapper from "./Modals/ModalWrapper";
import {
  AdvancedMarker,
  Map,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { SimpleLatLng } from "./SiteMap/Core";
import { Address } from "../services/axios/jobs";
import { useEffect, useState } from "react";
import PlaceAutocomplete, {
  extractAddressFromComponents,
} from "../pages/Admin/Jobs/PlaceAutocomplete";
import SubtleButton from "./Buttons/SubtleButton";
import AuthLabeledTextInput from "./Inputs/AuthLabeledTextInput";
import BasicInput from "./Inputs/BasicInput";

export default function SelectOnMap({
  currentLocation,
  currentAddress,
  onSelect,
  cancel,
}: {
  currentLocation?: SimpleLatLng;
  currentAddress?: Address;
  onSelect: SelectOnMapCallback;
  cancel: () => void;
}) {
  return (
    <ModalWrapper clickOff={cancel}>
      <div className="bg-white p-5 z-10 rounded-xl flex-col items-center">
        <h2 className="text-center font-bold">
          Click on the map or search to select the property address
        </h2>
        <ChoosePoint
          currentLocation={currentLocation}
          currentAddress={currentAddress}
          onSelect={onSelect}
        />
      </div>
    </ModalWrapper>
  );
}
type SelectOnMapResult = {
  address: Address | null;
  location: SimpleLatLng;
};
type SelectOnMapCallback = (result: SelectOnMapResult) => void;
/**
 * A component to select a point on a map
 */
export function ChoosePoint({
  currentLocation,
  currentAddress,
  onSelect,
}: {
  currentLocation?: SimpleLatLng;
  currentAddress?: Address;
  onSelect: SelectOnMapCallback;
}) {
  const [result, setResult] = useState<SelectOnMapResult | null>(
    currentLocation
      ? { location: currentLocation, address: currentAddress ?? null }
      : null,
  );
  return (
    <form
      className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2"
      onSubmit={() => onSelect(result!)}
    >
      <div className="col-span-2">
        <PlaceAutocomplete
          placeholder="Search (or paste an address)..."
          onPlaceSelect={(place, address, location) => {
            if (location?.lat) {
              setResult({
                location,
                address,
              });
            }
          }}
        />
      </div>
      <div>
        <Map
          id="property-site-map"
          mapId={"property-site-map"}
          mapTypeId="roadmap"
          style={{
            width: "550px",
            height: "550px",
            maxWidth: "100%",
          }}
          colorScheme="FOLLOW_SYSTEM"
          defaultCenter={
            currentLocation ?? { lat: 39.88156255, lng: -82.60261563 }
          } // random location roughly focusing the map on the US East Coast
          defaultTilt={0}
          defaultZoom={currentLocation ? 15 : 3}
          gestureHandling={"greedy"}
          reuseMaps={true}
          controlSize={34}
          disableDefaultUI={false}
          fullscreenControl={false}
        >
          {result?.location?.lat && result?.location?.lng && (
            <AdvancedMarker
              key={"advanced-marker"}
              position={result.location}
            />
          )}
          <ChoosePointListener
            position={result?.location}
            onSelect={(result) => setResult(result)}
          />
        </Map>
      </div>
      <div className="grid grid-cols-1">
        <div className="overflow-scroll">
          {AddressComponents.map((c) => (
            <AuthLabeledTextInput
              key={c.key}
              disabled={result?.location?.lat == null}
              required={c.required === true}
              label={c.label}
              value={result?.address?.[c.key] ?? ""}
              className="text-xs mt-0"
              inputClassName="text-sm"
              onInput={(e) =>
                setResult((current) => {
                  const target = e.target! as HTMLInputElement;
                  const result: SelectOnMapResult = {
                    location: current!.location!,
                    address: {
                      ...(current?.address ?? {}),
                      [c.key]: target.value,
                    },
                  };
                  return result;
                })
              }
            />
          ))}
          <div>
            <label className="text-xs my-2 block">
              Lat / Lng
              <div className="grid grid-cols-2 gap-1">
                <BasicInput
                  type="number"
                  step=".000000001"
                  name="lat"
                  value={result?.location.lat}
                  className="px-1 py-1 text-xs"
                  label={undefined}
                  disabled={undefined}
                  maxLength={undefined}
                  minLength={undefined}
                  onChange={undefined}
                  placeholder={undefined}
                  pattern={undefined}
                  required={true}
                  onInput={undefined}
                  testId={undefined}
                  defaultValue={undefined}
                  autoFocus={undefined}
                  onClick={undefined}
                />
                <BasicInput
                  type="number"
                  step=".000000001"
                  name="lng"
                  value={result?.location.lat}
                  className="px-1 py-1 text-xs"
                  label={undefined}
                  disabled={undefined}
                  maxLength={undefined}
                  minLength={undefined}
                  onChange={undefined}
                  placeholder={undefined}
                  pattern={undefined}
                  required={true}
                  onInput={undefined}
                  testId={undefined}
                  defaultValue={undefined}
                  autoFocus={undefined}
                  onClick={undefined}
                />
              </div>
            </label>
          </div>
          {/* <p className="text-gray-400 text-xs">Lat / Lng: {result?.location && <>{Math.round(result?.location.lat*100000)/100000} {Math.round(result?.location.lng*100000)/100000}</>}</p> */}
        </div>
        <div>
          <SubtleButton
            onClick={undefined}
            disabled={undefined}
            loading={undefined}
            loaderColor={undefined}
            loaderSize={undefined}
            label={undefined}
            className="w-full"
            testId={undefined}
            title={undefined}
            buttonRole="primary"
            type="submit"
          >
            Confirm
          </SubtleButton>
        </div>
      </div>
    </form>
  );
}
const AddressComponents: Array<{
  label: string;
  key: keyof Address;
  required?: boolean;
}> = [
  { label: "Street", key: "streetLineOne", required: true },
  { label: "Street (2)", key: "streetLineTwo" },
  { label: "City", key: "city", required: true },
  { label: "State", key: "state" },
  { label: "Potal Code", key: "zip", required: true },
  { label: "Country", key: "country" },
];
// Supports the above component with access to the useMap() hook.
function ChoosePointListener({
  onSelect,
  position,
}: {
  position?: SimpleLatLng | null;
  onSelect: (result: {
    address: Address | null;
    location: SimpleLatLng;
  }) => void;
}) {
  const map = useMap();
  const geoCodeLib = useMapsLibrary("geocoding");
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listener = map?.addListener("click", async (e: any) => {
      const location: SimpleLatLng = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      console.log(location);
      const geocoder = new geoCodeLib!.Geocoder();
      const results = await geocoder.geocode({ location });
      const r = results.results[0];
      const addr = extractAddressFromComponents(r);
      console.log(r, addr);
      onSelect({ address: addr, location });
    });
    return () => {
      listener?.remove();
    };
  }, [map, geoCodeLib, onSelect]);
  useEffect(() => {
    if (map && position) {
      map.setCenter(position);
    }
  }, [map, position]);
  return null;
}
